import { CommonModule } from "@angular/common";
import { Component, DestroyRef, ViewChild, computed, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Validators } from "@angular/forms";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { ButtonConfig } from "@template-ui/types";
import { EzFormInputType, InputConfig } from "@template-ui/types/ez-form.types";
import { ColDef } from "ag-grid-community";
import {
  ProgramPackContextEntry,
  ProgramPackResolutionResult,
  ProgramPackResolutionTraceEntry,
  ProgramPackScope,
  ProgramPackWorkspaceResult,
} from "./platform-program-pack-workspace.models";
import { PlatformProgramPackWorkspaceService } from "./platform-program-pack-workspace.service";

interface ProgramPackPreviewFormValues {
  configurationCode: string;
  jurisdictionCode: string;
  programCode: string;
  asOfUtc: string;
}

const DEFAULT_PREVIEW_CONTEXT: ProgramPackPreviewFormValues = {
  configurationCode: "program-pack",
  jurisdictionCode: "ca",
  programCode: "phms",
  asOfUtc: "",
};

@Component({
  selector: "phms-platform-program-pack-workspace",
  imports: [CommonModule, UiLibraryModule],
  templateUrl: "./platform-program-pack-workspace.component.html",
  styleUrl: "./platform-program-pack-workspace.component.scss",
})
export class PlatformProgramPackWorkspaceComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly workspaceService = inject(PlatformProgramPackWorkspaceService);

  @ViewChild("previewForm")
  private previewForm?: EzFormComponent;

  readonly isResolving = signal(false);
  readonly resolutionResult = signal<ProgramPackWorkspaceResult<ProgramPackResolutionResult> | null>(null);

  readonly pack = computed(() => this.resolutionResult()?.data ?? null);
  readonly selection = computed(() => this.pack()?.selection ?? null);
  readonly configurationVersion = computed(() => this.pack()?.resolution.version ?? null);
  readonly scopePrecedence = computed(() => this.pack()?.scopePrecedence ?? []);
  readonly resolutionTrace = computed(() => this.pack()?.resolution.trace ?? []);
  readonly contextEntries = computed<ProgramPackContextEntry[]>(() => toContextEntries(this.pack()?.context));
  readonly hasLoaded = computed(() => this.resolutionResult() !== null);

  readonly previewFormConfig: InputConfig[] = this.buildPreviewFormConfig();
  readonly scopePrecedenceColumnDefs: ColDef[] = this.buildScopePrecedenceColumnDefs();
  readonly resolutionTraceColumnDefs: ColDef[] = this.buildResolutionTraceColumnDefs();
  readonly contextColumnDefs: ColDef[] = this.buildContextColumnDefs();

  readonly previewActionButtons = computed<ButtonConfig[]>(() => [
    {
      id: "resolvePack",
      buttonText: this.isResolving() ? "Resolving..." : "Resolve Pack",
      disabled: this.isResolving(),
    },
    {
      id: "resetWorkspace",
      buttonText: "Reset",
      severity: "secondary",
      disabled: this.isResolving(),
    },
  ]);

  previewFormValues: ProgramPackPreviewFormValues = { ...DEFAULT_PREVIEW_CONTEXT };

  onPreviewActionButtonClick(button: ButtonConfig): void {
    switch (button.id) {
      case "resolvePack":
        this.resolvePack();
        break;
      case "resetWorkspace":
        this.resetWorkspace();
        break;
      default:
        break;
    }
  }

  resolvePack(): void {
    if (!this.ensureFormIsValid(this.previewForm)) {
      return;
    }

    this.isResolving.set(true);
    this.resolutionResult.set(null);

    this.workspaceService
      .resolvePack({
        configurationCode: this.previewFormValues.configurationCode.trim(),
        jurisdictionCode: normalizeOptionalText(this.previewFormValues.jurisdictionCode),
        programCode: normalizeOptionalText(this.previewFormValues.programCode),
        asOfUtc: normalizeOptionalText(this.previewFormValues.asOfUtc),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.resolutionResult.set(result);
        this.isResolving.set(false);
      });
  }

  resetWorkspace(): void {
    this.previewFormValues = { ...DEFAULT_PREVIEW_CONTEXT };
    this.previewForm?.clearErrors();
    this.isResolving.set(false);
    this.resolutionResult.set(null);
  }

  formatMessage(result: ProgramPackWorkspaceResult<unknown> | null): string {
    if (!result) {
      return "";
    }

    if (result.state === "resolved") {
      return "Resolved";
    }

    return result.error?.messages.join(" ") ?? "Program pack workspace unavailable.";
  }

  formatSelectionScope(): string {
    const selection = this.selection();
    if (!selection) {
      return "none";
    }

    return [normalizeOptionalText(selection.jurisdictionCode), normalizeOptionalText(selection.programCode)]
      .filter((value): value is string => value !== undefined)
      .join(" / ") || "global";
  }

  formatEffectiveWindow(): string {
    const version = this.configurationVersion();
    if (!version) {
      return "none";
    }

    const start = formatDateTimeLabel(version.effectiveStartUtc);
    const end = version.effectiveEndUtc ? formatDateTimeLabel(version.effectiveEndUtc) : "open";
    return `${start} to ${end}`;
  }

  private ensureFormIsValid(form: EzFormComponent | undefined): boolean {
    const formGroup = form?.theFormGroup;
    if (!formGroup || formGroup.valid) {
      return true;
    }

    formGroup.markAllAsTouched();
    return false;
  }

  private buildPreviewFormConfig(): InputConfig[] {
    return [
      {
        name: "configurationCode",
        type: EzFormInputType.TEXT,
        label: "Configuration",
        row: 1,
        width: "1x",
        validation: [Validators.required],
        validationMessages: {
          required: "Configuration code is required.",
        },
      },
      {
        name: "jurisdictionCode",
        type: EzFormInputType.TEXT,
        label: "Jurisdiction",
        row: 1,
        width: "1x",
      },
      {
        name: "programCode",
        type: EzFormInputType.TEXT,
        label: "Program",
        row: 1,
        width: "1x",
      },
      {
        name: "asOfUtc",
        type: EzFormInputType.TEXT,
        label: "As Of (UTC)",
        row: 1,
        width: "1x",
      },
    ];
  }

  private buildScopePrecedenceColumnDefs(): ColDef[] {
    return [
      {
        headerName: "Rank",
        width: 90,
        valueGetter: ({ node }) => (node?.rowIndex ?? 0) + 1,
      },
      { field: "scopeTypeCode", headerName: "Scope", flex: 1, minWidth: 180 },
      {
        field: "scopeKey",
        headerName: "Scope Key",
        flex: 1,
        minWidth: 180,
        valueFormatter: ({ value }) => (value as string | null | undefined) ?? "*",
      },
    ];
  }

  private buildResolutionTraceColumnDefs(): ColDef[] {
    return [
      { field: "precedenceRank", headerName: "Rank", width: 90 },
      { field: "scopeTypeCode", headerName: "Scope", flex: 1, minWidth: 180 },
      {
        field: "scopeKey",
        headerName: "Scope Key",
        flex: 1,
        minWidth: 180,
        valueFormatter: ({ value }) => (value as string | null | undefined) ?? "*",
      },
      { field: "matchedBindingCount", headerName: "Matched Bindings", width: 170 },
    ];
  }

  private buildContextColumnDefs(): ColDef[] {
    return [
      { field: "key", headerName: "Context Key", flex: 1, minWidth: 220 },
      {
        field: "value",
        headerName: "Resolved Value",
        flex: 2,
        minWidth: 260,
        valueFormatter: ({ value }) => (value as string | null | undefined) ?? "",
      },
    ];
  }
}

function toContextEntries(context: Record<string, string | null> | undefined): ProgramPackContextEntry[] {
  if (!context) {
    return [];
  }

  return Object.entries(context).map(([key, value]) => ({ key, value }));
}

function normalizeOptionalText(value: string | null | undefined): string | undefined {
  const normalized = value?.trim() ?? "";
  return normalized.length > 0 ? normalized : undefined;
}

function formatDateTimeLabel(value: string | null | undefined): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
